import axios, { InternalAxiosRequestConfig } from 'axios';

import { API_BASE_URL } from '../config/apiConfig';
import { getAuthGeneration, getAuthToken } from '../utils/auth';
import { captureAuthRecoverySnapshot, getAuthRevocationCoordinationState } from '../utils/authTransitions';

const csrfClient = axios.create({ baseURL: API_BASE_URL, withCredentials: true, timeout: 10000 });
let pendingToken: Promise<string> | null = null;

const getRequestUrl = (config: { url?: string; baseURL?: string }): URL | null => {
  try {
    const rawUrl = config.url ?? '';
    const target = /^([a-z][a-z\d+.-]*:)?\/\//i.test(rawUrl)
      ? rawUrl
      : `${(config.baseURL ?? API_BASE_URL).replace(/\/+$/, '')}/${rawUrl.replace(/^\/+/, '')}`;
    return new URL(target, window.location.origin);
  } catch {
    return null;
  }
};

export const isWiseFriendApiRequest = (config: { url?: string; baseURL?: string }): boolean => {
  const requestUrl = getRequestUrl(config);
  const apiUrl = new URL(API_BASE_URL, window.location.origin);
  return requestUrl?.origin === apiUrl.origin && requestUrl.pathname.startsWith('/api/');
};

const getCachedUser = (): string | null => {
  try {
    return localStorage.getItem('user');
  } catch {
    return null;
  }
};

const captureCookieSessionGuard = (coordinatedLogout = false): (() => boolean) => {
  const generation = getAuthGeneration();
  const cachedUser = getCachedUser();
  const logoutEpoch = coordinatedLogout ? captureAuthRecoverySnapshot().operationEpoch : null;
  return () => {
    if (generation === getAuthGeneration() && cachedUser === getCachedUser()) {
      return true;
    }
    // Normal logout clears visible auth immediately. Its existing revocation
    // operation blocks new sign-in until this request settles, so only that
    // exact pending operation may continue with its outgoing browser cookie.
    return coordinatedLogout && !getAuthToken() && getCachedUser() === null &&
      getAuthRevocationCoordinationState() === 'pending' &&
      captureAuthRecoverySnapshot().operationEpoch === logoutEpoch;
  };
};

const getCsrfToken = (): Promise<string> => {
  if (!pendingToken) {
    // Share simultaneous bootstraps to avoid racing first-time cookie creation.
    // Fetch again for later writes so login/session CSRF rotation cannot leave a
    // stale token cached in memory. This client has no auth/request interceptors.
    pendingToken = csrfClient.get<{ csrfToken: string }>('/api/auth/csrf/').then(({ data }) => {
      if (typeof data.csrfToken !== 'string' || !/^[a-zA-Z0-9]{64}$/.test(data.csrfToken)) {
        throw new Error('Unable to initialize request protection. Please try again.');
      }
      return data.csrfToken;
    }).finally(() => {
      pendingToken = null;
    });
  }
  return pendingToken;
};

export const protectCookieRequest = (config: InternalAxiosRequestConfig): void => {
  const method = String(config.method ?? 'get').toUpperCase();
  if (
    ['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method) ||
    !isWiseFriendApiRequest(config) ||
    config.headers.Authorization ||
    config.headers.get?.('Authorization')
  ) {
    return;
  }

  const adapter = axios.getAdapter(config.adapter ?? axios.defaults.adapter);
  const coordinatedLogout = method === 'POST' &&
    getRequestUrl(config)?.pathname === '/api/auth/logout/' && Boolean(config.signal) &&
    getAuthRevocationCoordinationState() === 'pending';
  const isSessionCurrent = captureCookieSessionGuard(coordinatedLogout);
  // Keep request interceptors synchronous: logout must capture its outgoing
  // Authorization header and auth snapshot before local state is cleared.
  config.adapter = async (requestConfig) => {
    const csrfToken = await getCsrfToken();
    requestConfig.cancelToken?.throwIfRequested();
    if (requestConfig.signal?.aborted || !isSessionCurrent()) {
      throw new axios.CanceledError(undefined, requestConfig);
    }
    requestConfig.headers.set('X-CSRFToken', csrfToken);
    return adapter(requestConfig);
  };
};

export const fetchWithCookieCsrf = (
  url: string,
  options: RequestInit & { headers: Record<string, string> },
): Promise<Response> => {
  const method = String(options.method ?? 'GET').toUpperCase();
  const hasAuthorization = Object.entries(options.headers)
    .some(([name, value]) => name.toLowerCase() === 'authorization' && Boolean(value));
  if (
    ['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method) ||
    !isWiseFriendApiRequest({ url }) || hasAuthorization || options.credentials === 'omit'
  ) {
    return fetch(url, options);
  }

  const isSessionCurrent = captureCookieSessionGuard();
  return getCsrfToken().then((csrfToken) => {
    if (options.signal?.aborted || !isSessionCurrent()) {
      throw new DOMException('The request was canceled because its session ended.', 'AbortError');
    }
    return fetch(url, {
      ...options,
      headers: { ...options.headers, 'X-CSRFToken': csrfToken },
    });
  });
};
